<template>
  <div class="assessment-type-management-container container">
    <el-card shadow="hover">
      <template #header>
        <div class="card-header">
          <h2>إدارة أنواع التقييمات</h2>
          <el-button type="primary" @click="handleCreate">
            <el-icon><Plus /></el-icon> إضافة نوع جديد
          </el-button>
        </div>
      </template>

      <!-- شرح للواجهة -->
      <el-collapse v-model="activeCollapse" style="margin-bottom: 20px;">
        <el-collapse-item name="1">
            <template #title>
                <el-icon><QuestionFilled /></el-icon><span style="margin-right: 8px; font-weight: bold;">لماذا هذه الواجهة مهمة؟ (اضغط للتوضيح)</span>
            </template>
            <div>
                <p>هذه الواجهة هي حجر الأساس لعملية رصد الدرجات. هنا، نقوم بتعريف <strong>الفئات</strong> أو "السلال" التي سنضع فيها الدرجات (مثل: واجب منزلي، اختبار قصير، امتحان نهائي). هذه الأنواع هي المكونات التي سنستخدمها لاحقًا في واجهة "سياسات التقييم" لتحديد "وصفة" حساب الدرجة النهائية لكل مادة.</p>
                <el-divider />
                <p><strong>شرح الحقول:</strong></p>
                <ul>
                    <li><strong>يدخل في المجموع النهائي (تقييم تجميعي):</strong> قم بتفعيله للتقييمات الرسمية التي تؤثر على شهادة الطالب (الامتحانات، المشاريع الكبرى). إذا لم تقم بتفعيله، ستكون التقييمات للممارسة والمتابعة فقط.</li>
                    <li><strong>يتطلب رفع ملف من الطالب عادةً:</strong> قم بتفعيله إذا كان هذا النوع من التقييم (مثل الواجبات أو المشاريع) يتطلب من الطالب رفع ملف عند تسليمه.</li>
                </ul>
            </div>
        </el-collapse-item>
      </el-collapse>


      <el-table :data="assessmentTypes" v-loading="loading" border style="width: 100%">
        <el-table-column prop="name" label="اسم النوع" />
        <el-table-column prop="description" label="الوصف" />
        <el-table-column prop="default_max_score" label="الدرجة الافتراضية" width="150" />
        
        <!-- --- العمود الجديد هنا --- -->
        <el-table-column label="يدخل في المجموع" width="180" align="center">
          <template #default="{ row }">
            <el-tag :type="row.is_summative ? 'success' : 'info'" effect="light" style="border-radius: 4px;">
              <el-icon style="vertical-align: middle; margin-right: 4px;">
                  <component :is="row.is_summative ? 'CircleCheck' : 'CircleClose'" />
              </el-icon>
              {{ row.is_summative ? 'نعم، تجميعي' : 'لا، تكويني' }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column label="الإجراءات" width="150" fixed="right">
          <template #default="{ row }">
            <el-button size="small" @click="handleEdit(row)">تعديل</el-button>
            <el-popconfirm
              title="هل أنت متأكد من حذف هذا النوع؟"
              confirm-button-text="نعم"
              cancel-button-text="لا"
              @confirm="handleDelete(row.id)"
            >
              <template #reference>
                <el-button size="small" type="danger">حذف</el-button>
              </template>
            </el-popconfirm>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- Dialog لإنشاء/تعديل النوع -->
    <el-dialog v-model="dialogVisible" :title="dialogTitle" width="40%" :close-on-click-modal="false">
      <el-form :model="form" ref="formRef" label-position="top" :rules="rules">
        <el-form-item label="اسم نوع التقييم" prop="name">
          <el-input v-model="form.name" placeholder="مثال: واجب منزلي، اختبار قصير" />
        </el-form-item>
        <el-form-item label="الوصف (اختياري)" prop="description">
          <el-input v-model="form.description" type="textarea" :rows="2" placeholder="شرح موجز للغرض من هذا النوع من التقييم."/>
        </el-form-item>
        <el-form-item label="الدرجة القصوى الافتراضية (اختياري)" prop="default_max_score">
          <el-input-number v-model="form.default_max_score" :min="0" />
          <p class="form-tip">هذه هي الدرجة التي ستظهر تلقائيًا عند إنشاء تقييم من هذا النوع.</p>
        </el-form-item>
        <el-checkbox v-model="form.is_summative" label="يدخل في المجموع النهائي (تقييم تجميعي)" border />
        <el-checkbox v-model="form.requires_submission_file" label="يتطلب رفع ملف من الطالب عادةً" border />
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">إلغاء</el-button>
          <el-button type="primary" @click="submitForm" :loading="formLoading">
            {{ form.id ? 'تحديث' : 'إنشاء' }}
          </el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import { ElMessage, ElPopconfirm } from 'element-plus';
import { Plus, QuestionFilled, CircleCheck, CircleClose } from '@element-plus/icons-vue';

export default {
  name: 'AssessmentTypeManagement',
  components: { Plus, ElPopconfirm, QuestionFilled, CircleCheck, CircleClose },
  data() {
    return {
      api: null,
      assessmentTypes: [],
      loading: false,
      activeCollapse: '',
      dialogVisible: false,
      formLoading: false,
      form: this.getInitialForm(),
      rules: {
        name: [{ required: true, message: 'اسم النوع مطلوب', trigger: 'blur' }],
      },
    };
  },
  computed: {
    dialogTitle() {
      return this.form.id ? 'تعديل نوع التقييم' : 'إنشاء نوع تقييم جديد';
    },
  },
  created() {
    this.api = axios.create({ baseURL: process.env.VUE_APP_API_BASE_URL || 'https://cale.caledonian.ly/api' });
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem('access_token');
      if (token) config.headers['Authorization'] = `Bearer ${token}`;
      return config;
    });
    this.fetchAssessmentTypes();
  },
  methods: {
    getInitialForm() {
      return { id: null, name: '', description: '', default_max_score: 10, is_summative: true, requires_submission_file: false };
    },
    async fetchAssessmentTypes() {
      this.loading = true;
      try {
        const response = await this.api.get('/assessment-types');
        this.assessmentTypes = response.data;
      } catch (error) {
        ElMessage.error('فشل في جلب أنواع التقييمات.');
      } finally {
        this.loading = false;
      }
    },
    handleCreate() {
      this.form = this.getInitialForm();
      this.dialogVisible = true;
    },
    handleEdit(row) {
      this.form = { ...row };
      this.dialogVisible = true;
    },
    async submitForm() {
      if (!this.$refs.formRef) return;
      await this.$refs.formRef.validate(async (valid) => {
        if (!valid) return;
        this.formLoading = true;
        try {
          if (this.form.id) {
            await this.api.put(`/assessment-types/${this.form.id}`, this.form);
            ElMessage.success('تم تحديث النوع بنجاح!');
          } else {
            await this.api.post('/assessment-types', this.form);
            ElMessage.success('تم إنشاء النوع بنجاح!');
          }
          this.dialogVisible = false;
          this.fetchAssessmentTypes();
        } catch (error) {
          const errorMsg = error.response?.data?.errors ? Object.values(error.response.data.errors)[0][0] : (error.response?.data?.message || 'فشلت العملية.');
          ElMessage.error(errorMsg);
        } finally {
          this.formLoading = false;
        }
      });
    },
    async handleDelete(id) {
      try {
        await this.api.delete(`/assessment-types/${id}`);
        ElMessage.success('تم حذف النوع بنجاح!');
        this.fetchAssessmentTypes();
      } catch (error) {
        const errorMsg = error.response?.data?.message || 'فشل في حذف النوع.';
        ElMessage.error(errorMsg);
      }
    },
  },
};
</script>

<style scoped>
.assessment-type-management-container { padding: 20px; }
.card-header { display: flex; justify-content: space-between; align-items: center; }
.el-checkbox { margin-top: 10px; margin-right: 15px !important; }
.form-tip {
    font-size: 12px;
    color: #909399;
    margin-top: 5px;
}
</style>